import { useRef, useState } from 'react'
import styles from './FilterItem.module.scss'
import {
    DotsThreeIcon,
    InfoIcon,
    PencilSimpleIcon,
    NotepadIcon,
    TrashIcon,
} from '@phosphor-icons/react'
import { packFilter, unpackFilters } from '../../../../Insights/Filter'
import { cloneDeep, isEqual } from 'lodash'
import { useWorkspaceMetadata } from '../../../../../api/hooks/workspaceMetadata/useWorkspaceMetadata'
import {
    ScoopCheckbox,
    ScoopMenu,
    ScoopMenuItem,
    ScoopText,
    ScoopTooltip,
    useToast,
} from '../../../../ui'
import { useModal, MODALS } from '../../../../modals'

export const FilterItem = ({
    filter,
    server,
    getWorkspaceMetadata,
    isActive,
    config,
    setConfig,
    chartState,
}) => {
    const [menuOpen, setMenuOpen] = useState(false)
    const [hovered, setHovered] = useState(false)
    const anchorRef = useRef()
    const toast = useToast()

    const { deleteFilter } = useWorkspaceMetadata()

    // Modal Manager hooks
    const infoModal = useModal(MODALS.EXPLORER.SAVED_FILTER_INFO)
    const deleteModal = useModal(MODALS.COMMON.DELETE)
    const editModal = useModal(MODALS.EXPLORER.EDIT_SAVED_FILTER)

    const handleDelete = () => {
        deleteFilter({
            filterKey: filter.savedFilterKey,
            onSuccess: () => toast.success('Success!'),
            onError: () => toast.error('Error deleting saved filter.'),
        })
    }

    const handleApplyFilter = () => {
        const newConfig = cloneDeep(config)
        const currentFilterList = unpackFilters(config.filter)
        const filtersToAdd = unpackFilters(filter.filter)
        newConfig.filter = packFilter(currentFilterList.concat(filtersToAdd))
        setConfig(newConfig)
        chartState.getResults(newConfig)
    }

    const isApplied = () => {
        const toCompare = unpackFilters(filter.filter)
        const configFilters = unpackFilters(config.filter)
        const matching = configFilters.filter((configFilter) =>
            toCompare.some((compare) => isEqual(compare, configFilter))
        )
        return matching.length === toCompare.length
    }

    const handleInfoClick = () => {
        if (!filter.filter) {
            toast.error('This filter appears to be corrupted or incomplete')
            return
        }
        infoModal.show({ filter })
        setMenuOpen(false)
    }

    const handleEditClick = () => {
        if (!filter.filter) {
            toast.error('This filter appears to be corrupted or incomplete')
            return
        }
        editModal.show({
            filter,
            server,
        })
        setMenuOpen(false)
    }

    const handleDeleteClick = () => {
        deleteModal.show({
            title: 'Saved Filter',
            description: 'Deleting this saved filter will remove it permanently from the workspace',
            onConfirm: handleDelete,
        })
        setMenuOpen(false)
    }

    return (
        <>
            <div
                className={styles.listItem}
                onMouseEnter={() => setHovered(true)}
                onMouseLeave={() => setHovered(false)}
            >
                <div className={styles.itemContent}>
                    <div className={styles.leftSection}>
                        {isActive && <ScoopCheckbox checked />}
                        <ScoopTooltip title={filter.filterName || ''}>
                            <span>
                                <ScoopText
                                    className={`${styles.filterName} ${isActive ? styles.filterNameWithCheckbox : ''}`}
                                >
                                    {filter.filterName}
                                </ScoopText>
                            </span>
                        </ScoopTooltip>
                    </div>
                    {(hovered || menuOpen) && (
                        <button
                            ref={anchorRef}
                            onClick={() => setMenuOpen(true)}
                            className={styles.menuButton}
                        >
                            <DotsThreeIcon size={16} weight="bold" />
                        </button>
                    )}
                </div>
                <ScoopMenu
                    open={menuOpen}
                    anchorEl={anchorRef.current}
                    onClose={() => setMenuOpen(false)}
                >
                    {!isActive && !isApplied() && (
                        <ScoopMenuItem
                            onClick={() => {
                                setMenuOpen(false)
                                handleApplyFilter()
                            }}
                            leftSection={<NotepadIcon size={20} className={styles.menuIcon} />}
                        >
                            Apply
                        </ScoopMenuItem>
                    )}
                    <ScoopMenuItem
                        onClick={handleInfoClick}
                        leftSection={<InfoIcon size={20} className={styles.menuIcon} />}
                    >
                        Info
                    </ScoopMenuItem>
                    <ScoopMenuItem
                        onClick={handleEditClick}
                        leftSection={<PencilSimpleIcon size={20} className={styles.menuIcon} />}
                    >
                        Edit
                    </ScoopMenuItem>
                    <ScoopMenuItem
                        onClick={handleDeleteClick}
                        leftSection={<TrashIcon size={20} className={styles.menuIcon} />}
                    >
                        Delete
                    </ScoopMenuItem>
                </ScoopMenu>
            </div>
        </>
    )
}
